import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import type { AdmissionRules } from "@/lib/prepare-evaluation";
import { Save, ShieldAlert, SlidersHorizontal } from "lucide-react";

export const Route = createFileRoute("/antagningskriterier")({
  head: () => ({
    meta: [
      { title: "Antagningskriterier — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AntagningskriterierPage,
});

const NUM_FIELDS: { key: keyof AdmissionRules; label: string; help: string; max: number }[] = [
  { key: "min_crl", label: "Min CRL", help: "Customer Readiness Level (1–9).", max: 9 },
  { key: "min_brl", label: "Min BRL", help: "Business Readiness Level (1–9).", max: 9 },
  { key: "min_tmrl", label: "Min TMRL", help: "Team Readiness Level (1–9).", max: 9 },
  { key: "min_srl", label: "Min SRL", help: "Sustainability Readiness Level (1–9).", max: 9 },
  { key: "min_innovativt", label: "Min Innovationshöjd", help: "Bedömning 0–3.", max: 3 },
  { key: "min_hallbar", label: "Min Hållbarhet", help: "Bedömning 0–3.", max: 3 },
  { key: "min_skalbar", label: "Min Skalbarhet", help: "Bedömning 0–3.", max: 3 },
  { key: "min_team", label: "Min Team-bedömning", help: "Bedömning 0–3.", max: 3 },
];

const BOOL_FIELDS: { key: keyof AdmissionRules; label: string; help: string }[] = [
  {
    key: "require_grundarteam",
    label: "Kräv grundarägt/grundardrivet",
    help: "Bolaget måste vara grundarägt och grundardrivet.",
  },
  {
    key: "require_bg_check",
    label: "Kräv godkänd bakgrundskontroll",
    help: "Företags-/personkontroll måste vara genomförd och OK.",
  },
  {
    key: "require_not_market_ready",
    label: "Kräv att bolaget ej är marknadsredo",
    help: "Bolaget får inte vara marknadsredo (Prepare-fasens målgrupp).",
  },
];

function AntagningskriterierPage() {
  return (
    <AppShell>
      <Inner />
    </AppShell>
  );
}

function Inner() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [rowId, setRowId] = useState<string | null>(null);
  const [rules, setRules] = useState<AdmissionRules>({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      setIsAdmin((roles ?? []).some((r) => r.role === "admin"));

      const { data } = await supabase
        .from("prepare_admission_criteria")
        .select("*")
        .eq("active", true)
        .maybeSingle();
      if (data) {
        setRowId(data.id);
        setRules((data.rules as AdmissionRules) ?? {});
        setNotes(data.notes ?? "");
      }
      setLoading(false);
    })();
  }, [user]);

  const updateNum = (key: keyof AdmissionRules, value: string) => {
    setRules((r) => ({
      ...r,
      [key]: value === "" ? null : Number(value),
    }));
  };

  const updateBool = (key: keyof AdmissionRules, value: boolean) => {
    setRules((r) => ({ ...r, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      active: true,
      rules: rules as never,
      notes,
      updated_by: user?.id ?? null,
    };
    if (rowId) {
      await supabase.from("prepare_admission_criteria").update(payload).eq("id", rowId);
    } else {
      const { data } = await supabase
        .from("prepare_admission_criteria")
        .insert(payload)
        .select("id")
        .single();
      if (data) setRowId(data.id);
    }
    setSaving(false);
    setSavedAt(new Date().toLocaleTimeString("sv-SE"));
  };

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground">Laddar…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-xl">
        <div className="rounded-2xl border border-border bg-card p-8 flex items-start gap-4">
          <ShieldAlert className="size-6 text-destructive shrink-0" />
          <div>
            <h1 className="font-display text-xl">Endast administratörer</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Antagningskriterierna styr den automatiska rekommendationen för
              alla Prepare-utvärderingar och kan endast hanteras av admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
          Konfiguration
        </div>
        <h1 className="font-display text-3xl">Antagningskriterier för Prepare</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Dessa trösklar driver den automatiska rekommendationen i varje Prepare-utvärdering.
          Lämna ett fält tomt för att hoppa över kriteriet. Kraven utvärderas mot värden i
          utvärderingsformuläret — om alla uppfylls föreslås <em>Rekommenderas</em>, annars
          <em> Rekommenderas med åtgärder</em> eller <em>Ej rekommenderas</em>.
        </p>
      </header>

      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <SlidersHorizontal className="size-4" />
          <h2 className="font-display text-lg">Numeriska trösklar</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {NUM_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {f.label}
              </span>
              <input
                type="number"
                min={0}
                max={f.max}
                step={1}
                value={(rules[f.key] as number | null | undefined) ?? ""}
                onChange={(e) => updateNum(f.key, e.target.value)}
                placeholder="—"
                className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
              />
              <span className="text-[11px] text-muted-foreground mt-1 block">
                {f.help}
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-lg mb-4">Obligatoriska kontroller</h2>
        <div className="space-y-3">
          {BOOL_FIELDS.map((f) => (
            <label
              key={f.key}
              className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-secondary/40"
            >
              <input
                type="checkbox"
                checked={Boolean(rules[f.key])}
                onChange={(e) => updateBool(f.key, e.target.checked)}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">{f.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{f.help}</div>
              </div>
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            Anteckningar (internt)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
          />
        </label>
      </section>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
        >
          <Save className="size-4" /> {saving ? "Sparar…" : "Spara kriterier"}
        </button>
        {savedAt && (
          <span className="text-xs text-muted-foreground">Sparat {savedAt}</span>
        )}
      </div>
    </div>
  );
}
